import * as d3 from 'd3';
import { drawMACD } from './findicators/MACD';
import { drawRSI } from './findicators/RSI';
import { drawStochasticOscillator } from './findicators/StochasticOscillator';
import { drawADX } from './findicators/ADX';
import { drawOBV } from './findicators/OBV';
import { drawCMF } from './findicators/CMF';
import { drawCCI } from './findicators/CCI';
import { drawWilliamsR } from './findicators/WilliamsR';
import { drawAROON } from './findicators/AROON';
import { drawMFI } from './findicators/MFI';
import { drawROC } from './findicators/ROC';
import { drawCMO } from './findicators/CMO';
import { drawRVI } from './findicators/RVI';
import { drawElderRay } from './findicators/ElderRay';
import { drawATR } from './findicators/ATR';
import { getIndicatorColor } from './findicators/colors';
import { buildSubYScale } from './../engine/scales';
import { drawXAxis, drawYAxis } from './../engine/axes';

const isValidNumber = (value) => !isNaN(value) && value !== null && isFinite(value);

const DEFAULT_DOMAINS = {
    macd: [-5, 5],
    rsi: [0, 100],
    stochastic: [0, 100],
    adx: [0, 100],
    mfi: [0, 100],
    williams_r: [-100, 0],
    cci: [-200, 200],
    roc: [-10, 10],
    cmo: [-100, 100],
    rvi: [-1, 1],
    atr: [0, 10],
};

const formatValue = (value, type) => {
    switch (type) {
        case 'rsi':
        case 'stochastic':
        case 'adx':
        case 'mfi':
            return d3.format('.1f')(value);
        default:
            return d3.format('.2f')(value);
    }
};

const getDomain = (type, name, findicatorsData) => {
    const key = findicatorsData?.[name.toLowerCase().replace(/-/g, '_')];
    const values = Array.isArray(key) ? key.filter(isValidNumber) : null;
    if (values && values.length > 0) {
        const min = d3.min(values);
        const max = d3.max(values);
        // Percentage-bounded oscillators keep their real [0,100]-style
        // domain even if the actual series never touches the edges --
        // matches how every trading platform draws RSI/Stochastic/ADX/MFI.
        if (DEFAULT_DOMAINS[type] && (DEFAULT_DOMAINS[type][0] === 0 || DEFAULT_DOMAINS[type][0] === -100)) {
            return DEFAULT_DOMAINS[type];
        }
        return [min, max];
    }
    return DEFAULT_DOMAINS[type] || [0, 100];
};

/**
 * Draws every active subchart indicator into pre-positioned panes. `panes`
 * comes from engine/layout.js (id, type, top, height) -- this module no
 * longer measures or mutates the SVG's own size (that was already
 * ineffective in the old implementation, since it called `.attr('width',
 * ...)` on a `<g>` rather than the real `<svg>` node).
 */
export const drawSubcharts = ({ root, panes, data, xScale, availableWidth, findicatorsData, theme }) => {
    const yScales = {};
    panes.forEach((pane, index) => {
        const { id: name, type, top, height } = pane;
        const values = findicatorsData?.[name.toLowerCase().replace(/-/g, '_')];
        const domain = getDomain(type, name, findicatorsData);
        const yScale = buildSubYScale(domain, height);
        yScales[name] = yScale;

        const group = root.append('g').attr('class', `subpane-${type}`).attr('transform', `translate(0,${top})`);

        if (index > 0) {
            group
                .append('line')
                .attr('x1', 0)
                .attr('x2', availableWidth)
                .attr('y1', 0)
                .attr('y2', 0)
                .attr('stroke', theme.grid.color)
                .attr('opacity', 0.4);
        }

        group
            .append('text')
            .attr('x', 0)
            .attr('y', -6)
            .attr('font-size', 11)
            .attr('font-weight', 500)
            .attr('fill', theme.axis.text)
            .attr('font-family', theme.typography.fontFamily)
            .text(name.toUpperCase());

        if (Array.isArray(values) && values.length > 0) {
            const last = values[values.length - 1];
            if (isValidNumber(last)) {
                group
                    .append('text')
                    .attr('x', availableWidth)
                    .attr('y', -6)
                    .attr('font-size', 10)
                    .attr('text-anchor', 'end')
                    .attr('fill', getIndicatorColor(name))
                    .attr('font-family', theme.typography.fontFamily)
                    .text(formatValue(last, type));
            }
        }

        const yAxisGroup = group.append('g').attr('class', 'y-axis');
        drawYAxis({ group: yAxisGroup, yScale, availableWidth, theme, formatter: d3.format('.1f'), tickCount: 3, showGrid: false });

        if (index === panes.length - 1) {
            const xAxisGroup = group.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${height})`);
            drawXAxis({ group: xAxisGroup, xScale, height, availableWidth, theme, showGrid: false });
        }

        const clipId = `subpane-clip-${index}`;
        root.select('defs').append('clipPath').attr('id', clipId).append('rect').attr('width', availableWidth).attr('height', height);
        const content = group.append('g').attr('class', 'indicator-content').attr('clip-path', `url(#${clipId})`);

        renderContent(content, type, name, data, xScale, yScale, values, theme);
    });
    return { yScales };
};

const renderContent = (group, type, name, data, xScale, yScale, values, theme) => {
    try {
        switch (type) {
            case 'macd':
                if (values?.macd && values?.signal && values?.histogram) drawMACD(group, data, xScale, yScale, values);
                break;
            case 'rsi':
                if (Array.isArray(values)) {
                    drawRSI(group, data, xScale, yScale, values, theme.indicators.rsi.line);
                    addReferenceLines(group, yScale, xScale, [
                        { value: 30, color: theme.indicators.rsi.oversold },
                        { value: 70, color: theme.indicators.rsi.overbought },
                    ]);
                }
                break;
            case 'stochastic':
                if (values?.stochastic_k && values?.stochastic_d) {
                    drawStochasticOscillator(group, data, xScale, yScale, values, theme.indicators.stochastic);
                    addReferenceLines(group, yScale, xScale, [
                        { value: 20, color: theme.indicators.stochastic.oversold },
                        { value: 80, color: theme.indicators.stochastic.overbought },
                    ]);
                }
                break;
            case 'adx':
                if (Array.isArray(values)) {
                    drawADX(group, data, xScale, yScale, values, theme.indicators.adx);
                    addReferenceLines(group, yScale, xScale, [{ value: 25, color: theme.bull.strong }]);
                }
                break;
            case 'atr':
                if (Array.isArray(values)) drawATR(group, data, xScale, yScale, values, theme.indicators.atr);
                break;
            case 'subchart': {
                const key = name.toLowerCase().replace(/-/g, '_');
                const color = getIndicatorColor(name);
                switch (key) {
                    case 'obv':
                        drawOBV(group, data, xScale, yScale, values, color);
                        break;
                    case 'cmf':
                        drawCMF(group, data, xScale, yScale, values, color);
                        addZeroLine(group, xScale, yScale, theme);
                        break;
                    case 'cci':
                        drawCCI(group, data, xScale, yScale, values, color);
                        addReferenceLines(group, yScale, xScale, [
                            { value: -100, color: theme.bear.strong },
                            { value: 100, color: theme.bear.strong },
                        ]);
                        break;
                    case 'williams_r':
                        drawWilliamsR(group, data, xScale, yScale, values, color);
                        addReferenceLines(group, yScale, xScale, [
                            { value: -20, color: theme.bear.strong },
                            { value: -80, color: theme.bear.strong },
                        ]);
                        break;
                    case 'aroon':
                        if (values?.aroon_up && values?.aroon_down) drawAROON(group, data, xScale, yScale, values);
                        break;
                    case 'mfi':
                        drawMFI(group, data, xScale, yScale, values, color);
                        addReferenceLines(group, yScale, xScale, [
                            { value: 20, color: theme.bear.strong },
                            { value: 80, color: theme.bear.strong },
                        ]);
                        break;
                    case 'roc':
                        drawROC(group, data, xScale, yScale, values, color);
                        addZeroLine(group, xScale, yScale, theme);
                        break;
                    case 'cmo':
                        drawCMO(group, data, xScale, yScale, values, color);
                        addZeroLine(group, xScale, yScale, theme);
                        break;
                    case 'rvi':
                        if (values?.rvi && values?.signal) drawRVI(group, data, xScale, yScale, values);
                        break;
                    case 'elder_ray':
                        if (values?.bull_power && values?.bear_power) drawElderRay(group, data, xScale, yScale, values);
                        break;
                    default:
                        break;
                }
                break;
            }
            default:
                break;
        }
    } catch (error) {
        console.warn(`Error rendering subpane ${name}:`, error);
    }
};

const addReferenceLines = (group, yScale, xScale, lines) => {
    lines.forEach(({ value, color }) => {
        group
            .append('line')
            .attr('x1', 0)
            .attr('x2', xScale.range()[1])
            .attr('y1', yScale(value))
            .attr('y2', yScale(value))
            .attr('stroke', color)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3,3')
            .attr('opacity', 0.45);
    });
};

const addZeroLine = (group, xScale, yScale, theme) => {
    group
        .append('line')
        .attr('x1', 0)
        .attr('x2', xScale.range()[1])
        .attr('y1', yScale(0))
        .attr('y2', yScale(0))
        .attr('stroke', theme.grid.color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2')
        .attr('opacity', 0.6);
};
